import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

export const stayMediaShareKey = (reservationId: string | null) => ["stay-media-share", reservationId];

/** 32 caracteres do alfabeto aceito pela edge function (`[A-Za-z0-9_-]`). */
function generateShareToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Devolve o token da galeria da reserva, criando-o na primeira vez.
 *
 * É uma query com efeito de escrita de propósito: o botão de WhatsApp precisa
 * do link já resolvido no clique — abrir `window.open` depois de um `await`
 * cai no bloqueador de popup.
 */
export function useStayMediaShareToken(reservationId: string | null) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: stayMediaShareKey(reservationId),
    enabled: Boolean(reservationId && clinicId),
    staleTime: Infinity,
    queryFn: async (): Promise<string | null> => {
      if (!reservationId || !clinicId) return null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase as any;

      const { data: existing, error: selectError } = await client
        .from("stay_media_shares")
        .select("token, revoked_at")
        .eq("stay_reservation_id", reservationId)
        .maybeSingle();
      if (selectError) throw selectError;
      if (existing && !existing.revoked_at) return existing.token as string;

      const token = generateShareToken();
      const { error: upsertError } = await client
        .from("stay_media_shares")
        .upsert(
          {
            clinic_id: clinicId,
            stay_reservation_id: reservationId,
            token,
            created_by: profile?.id ?? null,
            revoked_at: null,
          },
          { onConflict: "stay_reservation_id" },
        );
      if (upsertError) throw upsertError;
      return token;
    },
  });
}
